import { Tag, message, theme } from "antd";
import { SlotInfo } from "react-big-calendar";
import { useEffect, useState } from "react";
import {
  CalendarEvent,
  Company,
  CounsellingRequest,
  MAX_PAGE_SIZE,
  Training,
} from "../repository/data.default";
import {
  getCalendarEvents,
  getCounsellingRequest,
  getTraining,
} from "../repository/data.repository";
import dayjs from "dayjs";
import _ from "lodash";
import { getProblemMessage, retry } from "../utils/http";
import { QueryCondition, QueryOperation } from "../code/search-query/search-query";
import { dateRangeValue } from "../code/search-query/query-mapper";
import { InputObject } from "../controls/default";
import CalendarEventDialog from "./CalendarEventDialog";
import CounsellingManageDialog from "./counselling-requets/CounsellingManageDialog";
import TrainingManageDialog from "./trainings/TrainingManageDialog";
import { CalendarEventItem, originLabel } from "./CalendarView.default";
import { CalendarEventForm } from "./CalendarEventDialog.default";
import PlatformCalendar from "./calendar/PlatformCalendar";
import useCalendarEvents, { CalendarRange } from "../hooks/useCalendarEvents";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

type AdminEvent = CalendarEvent & {
  readonly __name?: string;
  readonly __type?: string;
};

export interface CalendarViewProps {
  readonly selectedCompany?: InputObject<Company> | null;
  readonly selectedDepartment?: InputObject | null;
  readonly selectedType?: InputObject | null;
  readonly openEvent?: "add" | "update" | "delete" | "preview" | null;
  readonly onEventClose?: () => void;
}

function CalendarView(props: CalendarViewProps) {
  const { token } = theme.useToken();
  const permissions = useSelector(authorizeSelectors.permissions);
  const [eventOpen, setEventOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventItem<AdminEvent>>(null!);
  const [selectedItem, setSelectedItem] = useState<CalendarEventForm | null>(null);
  const [eventType, setEventType] = useState<"add" | "update" | "delete" | "preview">("add");
  // Deep-link targets: the counselling request / training behind an origin event (opened from the
  // event preview).
  const [managedRequest, setManagedRequest] = useState<CounsellingRequest | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [managedTraining, setManagedTraining] = useState<Training | null>(null);
  const [trainingOpen, setTrainingOpen] = useState(false);

  const companyUid = props.selectedCompany?.value;
  const departmentUid = props.selectedDepartment?.value;
  const typeUid = props.selectedType?.value;

  // No company selected → nothing loads (the admin calendar is platform-wide, so we never preload everything).
  const calendar = useCalendarEvents<AdminEvent>({
    enabled: !!companyUid,
    reloadToken: `${companyUid ?? ""}|${departmentUid ?? ""}|${typeUid ?? ""}`,
    load: (range) => loadEvents(range),
  });

  useEffect(() => {
    if (props.openEvent) {
      setEventOpen(true);
      setEventType(props.openEvent);
      setSelectedItem({
        startDateTime: dayjs(),
        endDateTime: dayjs().add(1, "hour"),
      } as any);
    }
  }, [props.openEvent]);

  // Methods

  function loadEvents(range: CalendarRange): Promise<CalendarEventItem<AdminEvent>[]> {
    const conditions: QueryCondition[] = [
      // The server widens this companyUid to the company + its sub-companies (set-membership IN).
      { field: "companyUid", operation: QueryOperation.Equal, values: [companyUid!] },
      {
        field: "startDateTime",
        operation: QueryOperation.Between,
        values: dateRangeValue([dayjs(range.start), dayjs(range.end)]),
      },
    ];

    if (departmentUid)
      conditions.push({
        field: "departmentUid",
        operation: QueryOperation.Equal,
        values: [departmentUid],
      });

    if (typeUid)
      conditions.push({
        field: "typeUid",
        operation: QueryOperation.Equal,
        values: [typeUid],
      });

    return retry(() =>
      getCalendarEvents({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    ).then((result) =>
      result.data.map((dataItem) => ({
        title: dataItem.title,
        start: dayjs(dataItem.startDateTime).toDate(),
        end: dayjs(dataItem.endDateTime).toDate(),
        dataItem,
      })),
    );
  }

  // Events

  function onEventSelect(e: CalendarEventItem<AdminEvent>): void {
    setEventOpen(true);
    setEventType("preview");
    setSelectedEvent(e);
    setSelectedItem({
      // uid drives the participants load so preview lists all hosts + attendees (tagged by role).
      uid: e.dataItem!.uid,
      rowVersion: e.dataItem!.rowVersion,
      title: e.title,
      description: e.dataItem!.description,
      startDateTime: dayjs(e.start),
      endDateTime: dayjs(e.end),
      company: { display: e.dataItem!.companyName, value: e.dataItem!.companyUid },
      departments: (e.dataItem!.departments ?? []).map((d) => ({
        display: d.departmentName,
        value: d.departmentUid,
      })),
      type: { display: e.dataItem!.typeName, value: e.dataItem!.typeUid },
      originType: e.dataItem!.originType,
      originUid: e.dataItem!.originUid,
    });
  }

  function onEventAdd(e: SlotInfo): void {
    setEventOpen(true);
    setEventType("add");
    setSelectedItem({
      startDateTime: dayjs(e.start),
      endDateTime: dayjs(e.end),
    } as any);
  }

  function onEventMode(mode: "update" | "delete"): void {
    setEventOpen(true);
    setEventType(mode);
    setSelectedItem({
      uid: selectedEvent.dataItem!.uid,
      rowVersion: selectedEvent.dataItem!.rowVersion,
      title: selectedEvent.title,
      description: selectedEvent.dataItem!.description,
      startDateTime: dayjs(selectedEvent.start),
      endDateTime: dayjs(selectedEvent.end),
      company: { display: selectedEvent.dataItem!.companyName, value: selectedEvent.dataItem!.companyUid },
      departments: (selectedEvent.dataItem!.departments ?? []).map((d) => ({
        display: d.departmentName,
        value: d.departmentUid,
      })),
      type: { display: selectedEvent.dataItem!.typeName, value: selectedEvent.dataItem!.typeUid },
    });
  }

  function onEventFinish(): void {
    setEventOpen(false);
    calendar.reload();
    props.onEventClose?.();
  }

  function onEventCancel(): void {
    setEventOpen(false);
    props.onEventClose?.();
  }

  // Deep-link from an origin event → open its originating record's Manage dialog in place
  // (counselling request or training, by origin type).
  function onViewOrigin(originUid: string, originType: string): void {
    if (originType === "Training") {
      getTraining(originUid)
        .then((training) => {
          if (!training) return;
          setEventOpen(false);
          setManagedTraining(training);
          setTrainingOpen(true);
        })
        .catch((err) =>
          message.error(getProblemMessage(err) || "Failed to open the originating training."),
        );
      return;
    }
    getCounsellingRequest(originUid)
      .then((request) => {
        if (!request) return;
        setEventOpen(false);
        setManagedRequest(request);
        setManageOpen(true);
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to open the originating request."),
      );
  }

  function onManageFinish(): void {
    setManageOpen(false);
    calendar.reload();
  }

  function onTrainingFinish(): void {
    setTrainingOpen(false);
    calendar.reload();
  }

  return (
    <>
      <PlatformCalendar<AdminEvent>
        events={calendar.events}
        date={calendar.date}
        view={calendar.view}
        onNavigate={calendar.onNavigate}
        onView={calendar.onView}
        selectable={permissions.can("CalendarEvents", "create")}
        onSelectSlot={permissions.can("CalendarEvents", "create") ? onEventAdd : undefined}
        onSelectEvent={permissions.can("CalendarEvents", "view") ? onEventSelect : undefined}
        emptyHint={!companyUid ? "Select a company to view its calendar" : undefined}
        eventStyle={(item) =>
          item.dataItem?.originType ? { backgroundColor: token.colorPrimaryBorder } : undefined
        }
        agendaDescription={(item) => {
          const text = (item.dataItem?.description ?? "").replace(/\s+/g, " ").trim();
          return (
            <span title={text}>
              {_.truncate(text, { length: 60 + 3, omission: "..." })}
            </span>
          );
        }}
        renderEvent={(item) => {
          const calendarEvent = item.dataItem!;
          const origin = originLabel(calendarEvent.originType);
          const departmentNames = (calendarEvent.departments ?? [])
            .map((d) => d.departmentName)
            .filter(Boolean)
            .join(", ");
          const meta = [calendarEvent.companyName, departmentNames]
            .filter((f) => !!f)
            .join(" · ");
          const timeRange = `${dayjs(calendarEvent.startDateTime).format("HH:mm")} – ${dayjs(calendarEvent.endDateTime).format("HH:mm")}`;
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-medium leading-tight">{calendarEvent.title}</span>
              <span className="truncate text-[11px] leading-tight opacity-70">{timeRange}</span>
              {origin && (
                <Tag
                  color="purple"
                  bordered={false}
                  className="mr-0 self-start rounded px-1 py-0 text-[10px] leading-tight"
                >
                  {origin}
                </Tag>
              )}
              {meta && <span className="truncate text-[11px] leading-tight opacity-80">{meta}</span>}
              {!!calendarEvent.hosts?.length && (
                <span className="truncate text-[11px] leading-tight opacity-80">
                  {calendarEvent.hosts.map((h) => h.userUserName).join(", ")}
                </span>
              )}
            </span>
          );
        }}
      />
      <CalendarEventDialog
        open={eventOpen}
        mode={eventType}
        initialValues={selectedItem}
        onMode={onEventMode}
        onFinish={onEventFinish}
        onCancel={onEventCancel}
        onViewOrigin={onViewOrigin}
      />
      <CounsellingManageDialog
        open={manageOpen}
        record={managedRequest ?? undefined}
        onFinish={onManageFinish}
        onCancel={() => setManageOpen(false)}
      />
      <TrainingManageDialog
        open={trainingOpen}
        record={managedTraining ?? undefined}
        onFinish={onTrainingFinish}
        onCancel={() => setTrainingOpen(false)}
      />
    </>
  );
}

export default CalendarView;
